/**
 * Nouveau bandeau KPIs CFO — 8 KPIs essentiels + cliquables (Fév 2026).
 * Spec CFO : "Solde disponible, Entrées, Sorties, Variation nette, Rapprochées,
 * À traiter, Écart à vérifier, Frais bancaires" avec libellés simples.
 *
 * KPIs cliquables → callback onFilterChange(filter) qui applique un filtre visuel
 * sur le tableau des opérations + scroll vers le tableau.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Wallet, ArrowUpRight, ArrowDownRight, TrendingUp, CheckCircle2,
  AlertTriangle, Landmark, Lock, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

// Retourne le dernier jour d'une période "yyyy-MM" (ou "aujourd'hui" si c'est le mois courant).
// Format retourné : "dd/MM/yyyy"
const periodEndLabel = (period) => {
  if (!period || typeof period !== 'string') return '';
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return '';
  const now = new Date();
  const isCurrentMonth = y === now.getFullYear() && m === (now.getMonth() + 1);
  const lastDay = isCurrentMonth ? now : new Date(y, m, 0); // day 0 of next month = last day of this month
  return `${String(lastDay.getDate()).padStart(2, '0')}/${String(lastDay.getMonth() + 1).padStart(2, '0')}/${lastDay.getFullYear()}`;
};

const KPI_DEFS = [
  { key: 'solde', label: 'Solde fin de période',
    sub: (m, ctx) => {
      const endLabel = periodEndLabel(ctx?.period);
      const parts = [`Au ${endLabel || 'fin de période'}`];
      const live = Number(ctx?.currentBalance || 0);
      const closing = Number(m.solde_disponible || 0);
      // Si la période est passée ET le solde live diffère du solde de clôture, on l'indique
      if (live && Math.abs(live - closing) > 100) {
        parts.push(`Solde actuel : ${fmt(live)} XOF`);
      }
      return parts.join(' · ');
    },
    val: (m) => m.solde_disponible, unit: 'XOF', icon: Wallet, color: 'blue',
    filter: null },
  { key: 'entrees', label: 'Entrées du mois', sub: (m) => `Encaissements sur la période`,
    val: (m) => m.entrees, unit: 'XOF', icon: ArrowUpRight, color: 'emerald',
    filter: { direction: 'credit' } },
  { key: 'sorties', label: 'Sorties du mois', sub: () => 'Décaissements sur la période',
    val: (m) => m.sorties, unit: 'XOF', icon: ArrowDownRight, color: 'rose',
    filter: { direction: 'debit' } },
  { key: 'net', label: 'Variation nette', sub: () => 'Variation de trésorerie du mois',
    val: (m) => m.variation_nette, unit: 'XOF', icon: TrendingUp,
    color: (m) => (m.variation_nette >= 0 ? 'emerald' : 'rose'),
    filter: null,
    sign: true },
  { key: 'rapprochees', label: 'Rapprochées', sub: (m) => `${m.rapprochees_count} / ${m.total_tx} transactions rapprochées`,
    val: (m) => m.rapprochees_pct, unit: '%', icon: CheckCircle2, color: 'emerald',
    filter: { reconciliation_status: 'reconciled' } },
  { key: 'traiter', label: 'À traiter', sub: () => 'À pointer ou à justifier',
    val: (m) => m.a_traiter_count, unit: 'tx', icon: AlertTriangle, color: 'amber',
    filter: { reconciliation_status: 'to_match' } },
  { key: 'ecart', label: 'Écart à vérifier', sub: () => 'Contrôle de cohérence du relevé',
    val: (m) => m.ecart_solde, unit: 'XOF',
    icon: Landmark, color: (m) => (Math.abs(m.ecart_solde || 0) > 100 ? 'rose' : 'slate'),
    filter: { reconciliation_status: 'discrepancy' } },
  { key: 'frais', label: 'Frais bancaires', sub: () => 'Commissions, tenue, frais chèque',
    val: (m) => m.frais_bancaires, unit: 'XOF', icon: Landmark, color: 'violet',
    filter: { type: 'frais_bancaires' } },
];

const COLOR_CLASSES = {
  blue: 'from-blue-50 to-white border-blue-200 text-blue-600',
  emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-600',
  rose: 'from-rose-50 to-white border-rose-200 text-rose-600',
  amber: 'from-amber-50 to-white border-amber-200 text-amber-600',
  violet: 'from-violet-50 to-white border-violet-200 text-violet-600',
  slate: 'from-slate-50 to-white border-slate-200 text-slate-600',
};

const CLOSURE_BADGES = {
  ready: { label: 'Prêt pour clôture', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  review: { label: 'À vérifier', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  blocked: { label: 'Clôture bloquée', color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

export default function BankCFODashboardCards({ accountId, period, currentBalance, onFilterChange, refreshKey, onClosureStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/bank/analytics/full-dashboard/${accountId}`, {
        params: { period },
      });
      setData(res);
      if (onClosureStatusChange && res?.main?.closure_status) {
        onClosureStatusChange(res.main.closure_status);
      }
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [accountId, period, onClosureStatusChange]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  if (!data) return null;
  const m = data.main;

  const handleKpiClick = (kpi) => {
    if (kpi.filter && onFilterChange) {
      onFilterChange({ ...kpi.filter, __label: kpi.label });
      // Scroll vers le tableau
      const table = document.querySelector('[data-testid="bank-tx-table"]');
      if (table) table.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-3" data-testid="bank-cfo-dashboard">
      {/* Section Alertes (si présentes) */}
      {data.alerts?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="bank-cfo-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <span className="text-sm font-semibold text-amber-900">Alertes CFO ({data.alerts.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.alerts.map(a => (
              <button
                key={a.key}
                onClick={() => a.filter && Object.keys(a.filter).length > 0 && handleKpiClick({ filter: a.filter, label: a.label })}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${a.severity === 'high' ? 'bg-rose-100 text-rose-700 border-rose-300 hover:bg-rose-200' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
                data-testid={`bank-alert-${a.key}`}
              >
                {a.label} · {a.count}{a.amount > 0 ? ` · ${fmt(a.amount)} XOF` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 8 KPIs première ligne (cliquables) */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
        {KPI_DEFS.map(kpi => {
          const color = typeof kpi.color === 'function' ? kpi.color(m) : kpi.color;
          const value = kpi.val(m);
          const displayValue = kpi.sign && value > 0 ? `+${fmt(value)}` : fmt(value);
          return (
            <button
              key={kpi.key}
              onClick={() => handleKpiClick(kpi)}
              disabled={!kpi.filter}
              className={`text-left rounded-xl border bg-gradient-to-b p-3 hover:shadow-md transition-shadow ${COLOR_CLASSES[color] || COLOR_CLASSES.slate} ${kpi.filter ? 'cursor-pointer' : 'cursor-default'}`}
              data-testid={`bank-kpi-${kpi.key}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold">
                <kpi.icon className="h-3 w-3" />
                <span className="truncate">{kpi.label}</span>
              </div>
              <div className="text-lg font-bold text-slate-900 mt-1 tabular-nums">
                {displayValue}
                {kpi.unit ? <span className="text-[10px] font-normal text-slate-500 ml-1">{kpi.unit}</span> : null}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{kpi.sub(m, { period, currentBalance })}</div>
            </button>
          );
        })}
      </div>

      {/* Statut clôture (KPI #10 en badge séparé) */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Lock className="h-3.5 w-3.5" />
          <span>Statut clôture :</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${CLOSURE_BADGES[m.closure_status]?.color || 'bg-slate-100 text-slate-700 border-slate-200'}`}
                data-testid="bank-closure-status">
            {CLOSURE_BADGES[m.closure_status]?.label || m.closure_status}
          </span>
        </div>
        <button onClick={fetchData} disabled={loading}
                className="text-xs inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>
    </div>
  );
}
